<template>
  <div class="vendor-action-dock" :class="{ 'vendor-action-dock--open': open }">
    <button
      type="button"
      class="vendor-action-dock__button vendor-action-dock__button--delete"
      :aria-label="deleteLabel"
      :disabled="deleteDisabled || deleting"
      @click="emitDelete"
    >
      <i v-if="!deleting" class="ri-delete-bin-6-line"></i>
      <i v-else class="ri-loader-4-line vendor-action-dock__spinner"></i>
    </button>
    <button
      type="button"
      class="vendor-action-dock__button vendor-action-dock__button--edit"
      :aria-label="editLabel"
      @click="emitEdit"
    >
      <i class="ri-edit-line"></i>
    </button>
    <button
      type="button"
      class="vendor-action-dock__button vendor-action-dock__button--main"
      :aria-label="open ? closeLabel : openLabel"
      @click="open = !open"
    >
      <i :class="open ? 'ri-close-line' : 'ri-more-2-fill'"></i>
    </button>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { ref } from 'vue'

defineProps({
  openLabel: {
    type: String,
    default: 'Show actions'
  },
  closeLabel: {
    type: String,
    default: 'Close actions'
  },
  editLabel: {
    type: String,
    default: 'Edit'
  },
  deleteLabel: {
    type: String,
    default: 'Delete'
  },
  deleting: {
    type: Boolean,
    default: false
  },
  deleteDisabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['edit', 'delete'])
const open = ref(false)

const emitEdit = () => {
  open.value = false
  emit('edit')
}

const emitDelete = () => {
  emit('delete')
}
</script>

<style scoped>
.vendor-action-dock {
  position: relative;
  display: grid;
  width: 2rem;
  height: 2rem;
  place-items: center;
}

.vendor-action-dock__button {
  position: absolute;
  display: flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #1f2a44;
  font-size: 0.94rem;
  line-height: 1;
  box-shadow: 0 8px 18px rgba(91, 70, 56, 0.08);
  opacity: 0;
  pointer-events: none;
  transform: translateX(0) scale(0.35) rotate(-18deg);
  transform-origin: center;
  transition:
    opacity 0.18s ease,
    transform 0.28s cubic-bezier(.34, 1.56, .64, 1),
    color 0.18s ease,
    border-color 0.18s ease,
    background-color 0.18s ease,
    box-shadow 0.18s ease;
}

.vendor-action-dock__button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.vendor-action-dock__button--main {
  z-index: 3;
  opacity: 1;
  pointer-events: auto;
  transform: translateX(0) scale(1) rotate(0);
  transition:
    transform 0.34s cubic-bezier(.34, 1.56, .64, 1),
    color 0.18s ease,
    border-color 0.18s ease,
    background-color 0.18s ease;
}

.vendor-action-dock__button--main:hover,
.vendor-action-dock__button--edit:hover {
  border-color: #ff6b00;
  color: #ff6b00;
}

.vendor-action-dock__button--delete {
  color: #dc2626;
}

.vendor-action-dock__button--delete:hover:not(:disabled) {
  border-color: #fecaca;
  background: #fef2f2;
  color: #dc2626;
}

.vendor-action-dock--open .vendor-action-dock__button--main {
  transform: scale(1.05) rotate(180deg);
}

.vendor-action-dock--open .vendor-action-dock__button--edit,
.vendor-action-dock--open .vendor-action-dock__button--delete {
  opacity: 1;
  pointer-events: auto;
  animation: vendor-action-pop 0.5s cubic-bezier(.22, 1.55, .36, 1) both;
}

.vendor-action-dock--open .vendor-action-dock__button--edit {
  --action-x: -2.8rem;
  transform: translateX(-2.8rem) scale(1) rotate(0);
}

.vendor-action-dock--open .vendor-action-dock__button--delete {
  --action-x: -5.45rem;
  animation-delay: 0.04s;
  transform: translateX(-5.45rem) scale(1) rotate(0);
}

.vendor-action-dock__spinner {
  animation: vendor-action-spin 0.8s linear infinite;
}

@keyframes vendor-action-pop {
  0% {
    opacity: 0;
    transform: translateX(0) scale(0.35) rotate(-18deg);
  }
  64% {
    opacity: 1;
    transform: translateX(var(--action-x, -2.8rem)) scale(1.16) rotate(4deg);
  }
  82% {
    transform: translateX(var(--action-x, -2.8rem)) scale(0.94) rotate(-2deg);
  }
  100% {
    opacity: 1;
    transform: translateX(var(--action-x, -2.8rem)) scale(1) rotate(0);
  }
}

@keyframes vendor-action-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
